import {
  AudioPresets,
  VideoPresets,
  type RoomConnectOptions,
  type RoomOptions,
  type TrackPublishDefaults,
} from 'livekit-client';

/**
 * Rough device class. Used only to pick publish ceilings, so a wrong guess
 * costs a little quality rather than breaking the call.
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  // iPadOS reports itself as a Mac, hence the touch-point check.
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || iPadOS || coarse;
}

export function isSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
}

/**
 * Publish ceilings.
 *
 * Phones cap at 360p/~350kbps: a two-person call then costs roughly 0.4-0.8
 * Mbps each way on 4G, and the simulcast ladder lets LiveKit drop to 180p or
 * audio-only on 3G without the publisher renegotiating anything.
 */
function publishDefaults(mobile: boolean): TrackPublishDefaults {
  return {
    simulcast: true,
    videoSimulcastLayers: mobile
      ? [VideoPresets.h180, VideoPresets.h360]
      : [VideoPresets.h180, VideoPresets.h360, VideoPresets.h720],
    videoEncoding: mobile
      ? { maxBitrate: 350_000, maxFramerate: 24 }
      : { maxBitrate: 1_200_000, maxFramerate: 30 },
    screenShareEncoding: {
      maxBitrate: mobile ? 800_000 : 2_000_000,
      maxFramerate: 15,
    },
    // H.264 hits the hardware encoder on iPhones and most Android handsets,
    // which costs far less battery and CPU than VP8. VP8 stays enabled
    // server-side as the fallback for anything that cannot negotiate H.264.
    videoCodec: 'h264',
    // DTX stops sending during silence; RED adds redundant audio packets so
    // speech survives the 10-15% loss that is normal on a weak mobile link.
    dtx: true,
    red: true,
    audioPreset: AudioPresets.speech,
    stopMicTrackOnMute: false,
  };
}

/** The devices picked on the pre-join screen, if the user changed them. */
export interface DeviceChoices {
  videoDeviceId?: string;
  audioDeviceId?: string;
}

export function buildRoomOptions(devices: DeviceChoices = {}): RoomOptions {
  const mobile = isMobileDevice();

  return {
    // Subscribe at the resolution each tile is actually rendered at, so a phone
    // never pulls a 720p layer into a 200px tile.
    adaptiveStream: true,
    // Stop forwarding layers nobody is currently watching.
    dynacast: true,
    videoCaptureDefaults: {
      resolution: mobile ? VideoPresets.h360.resolution : VideoPresets.h720.resolution,
      // Carry the pre-join choice through. Left undefined, LiveKit opens the
      // system default, which is how a chosen external webcam gets silently
      // ignored the moment the call starts.
      ...(devices.videoDeviceId ? { deviceId: devices.videoDeviceId } : {}),
    },
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      ...(devices.audioDeviceId ? { deviceId: devices.audioDeviceId } : {}),
    },
    publishDefaults: publishDefaults(mobile),
    disconnectOnPageLeave: true,
  };
}

export function buildConnectOptions(): RoomConnectOptions {
  return {
    autoSubscribe: true,
    // Give a slow 3G handshake room to finish before giving up.
    maxRetries: 5,
    peerConnectionTimeout: 20_000,
    websocketTimeout: 20_000,
  };
}
